/**
 * Client-side rate limiting utilities
 * Prevents abuse and reduces server load by throttling/debouncing operations
 */

/**
 * Creates a throttled function that only invokes func at most once per specified wait time
 * @param {Function} func - The function to throttle
 * @param {number} wait - The throttle wait time in milliseconds
 * @returns {Function} - The throttled function
 */
export const throttle = (func, wait) => {
  let inThrottle;
  let lastResult;

  return function(...args) {
    const context = this;

    if (!inThrottle) {
      inThrottle = true;
      lastResult = func.apply(context, args);

      setTimeout(() => {
        inThrottle = false;
      }, wait);
    }

    return lastResult;
  };
};

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked
 * @param {Function} func - The function to debounce
 * @param {number} wait - The debounce wait time in milliseconds
 * @returns {Function} - The debounced function with a cancel method
 */
export const debounce = (func, wait) => {
  let timeoutId;

  const debounced = function(...args) {
    const context = this;

    clearTimeout(timeoutId);

    return new Promise((resolve) => {
      timeoutId = setTimeout(() => {
        resolve(func.apply(context, args));
      }, wait);
    });
  };

  // Add cancel method to clear pending debounced invocations
  debounced.cancel = () => {
    clearTimeout(timeoutId);
  };

  return debounced;
};

/**
 * Rate limiter class for tracking and limiting operations per time window
 * Useful for preventing rapid-fire API calls
 */
export class RateLimiter {
  constructor(maxCalls, windowMs) {
    this.maxCalls = maxCalls; // Maximum calls allowed
    this.windowMs = windowMs; // Time window in milliseconds
    this.calls = []; // Array of timestamps for calls
  }

  /**
   * Check if an operation can proceed based on rate limit
   * @returns {boolean} - true if operation can proceed, false if rate limited
   */
  canProceed() {
    const now = Date.now();

    // Remove calls outside the current time window
    this.calls = this.calls.filter(timestamp => now - timestamp < this.windowMs);

    // Check if we're under the limit
    if (this.calls.length < this.maxCalls) {
      this.calls.push(now);
      return true;
    }

    return false;
  }

  /**
   * Get time until next available call slot
   * @returns {number} - Milliseconds until next call can proceed
   */
  getTimeUntilNextCall() {
    if (this.calls.length === 0) return 0;

    const oldestCall = this.calls[0];
    const timeElapsed = Date.now() - oldestCall;
    const timeRemaining = this.windowMs - timeElapsed;

    return Math.max(0, timeRemaining);
  }

  /**
   * Reset the rate limiter
   */
  reset() {
    this.calls = [];
  }
}

/**
 * Creates a rate-limited version of an async function
 * @param {Function} func - The async function to rate limit
 * @param {number} maxCalls - Maximum calls allowed in time window
 * @param {number} windowMs - Time window in milliseconds
 * @returns {Function} - Rate-limited function
 */
export const rateLimitAsync = (func, maxCalls, windowMs) => {
  const limiter = new RateLimiter(maxCalls, windowMs);

  return async function(...args) {
    if (!limiter.canProceed()) {
      const waitTime = limiter.getTimeUntilNextCall();
      throw new Error(`Rate limit exceeded. Please wait ${Math.ceil(waitTime / 1000)} seconds.`);
    }

    return await func.apply(this, args);
  };
};

/**
 * Pre-configured rate limiters for common use cases
 */
export const RateLimitPresets = {
  // Auth operations: 5 attempts per minute
  auth: () => new RateLimiter(5, 60000),

  // Like operations: 10 per minute
  likes: () => new RateLimiter(10, 60000),

  // Comment creation: 5 per minute
  comments: () => new RateLimiter(5, 60000),

  // Profile updates: 3 per minute
  profile: () => new RateLimiter(3, 60000),

  // Video uploads: 3 per hour
  uploads: () => new RateLimiter(3, 3600000),
};
